
import { defineComponent, toRef, computed, ref, PropType, watch } from "vue";
import { OrderProduct } from "@/models/Order";
import moment from "moment";
import InputNumber, { InputNumberBlurEvent } from "primevue/inputnumber";
import { useStore } from "@/store";
import swal from "sweetalert2";
import { useRouter } from "vue-router";
import { NotAvailableProducts } from "@/models/Order";
import q from "q";
import { useI18n } from "vue-i18n";
import { CouponLimitationType, CouponDiscountType } from "@/models/Enums";
import { Coupon } from "@/models/Product";
import { VIRTUAL_SLOT_ID } from '@/models/Constants';

export default defineComponent({
  props: {
    productProp: {
      type: OrderProduct,
      required: true,
    },
    currencySign: {
      type: String,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    active: {
      type: Boolean,
      required: true,
    },
    notAvailableProducts: {
      type: Array as PropType<NotAvailableProducts[]>,
      required: true,
    },
  },
  components: {
    InputNumber,
  },
  async setup(props) {
    const { t } = useI18n();
    const product = toRef(props, "productProp");
    const store = useStore();
    const qtyOldValue = ref(0);
    const couponExist = computed(() => product.value.priceLine.couponId);
    const isImmutable = computed(
      () =>
        couponExist.value &&
        store.state.order.isReserved &&
        product.value.priceLine.orderProductCustomerId
    );
    const isVoucherItem = computed(() => !!store.state.scanValue);
    const canEditDiscountPermission = computed(
      () => store.getters.canEditDiscount
    );
    const canEditDiscount = computed(() => {
      return canEditDiscountPermission.value && !couponExist.value;
    });
    const changeUnitPrice = computed(
      () => store.getters.changeUnitPrice && !couponExist.value
    );
    const router = useRouter();

    function changeSlotByPriceDuration(index: number) {
      if (product.value.notValidCoupon) {
        return;
      }
      const productStore = store.state.order.products[index];
      console.log(index, productStore);

      store.commit("toChangeInBasketProduct", index);
      const slotId = productStore.slotId == VIRTUAL_SLOT_ID ? productStore.slotFakeId : productStore.slotId;
      router.push({
        name: "calendar",
        params: {
          productId: productStore.id,
          slotId: slotId,
          date: moment(productStore.date).format("YYYY-MM-DD"),
          priceId: productStore.priceId,
        },
      });
    }
    async function minusQuontity(index: number) {
      if (product.value.notValidCoupon) {
        return;
      }
      if (product.value.cardCode) {
        swal.fire({
            icon: "error",
            text: t("cashier.alert.external-coupon"),
          });
        return;
      }
      await store.dispatch("minusQuontityToOrderProduct", index);
    }
    function plusQuontity(index: number) {
      if (product.value.notValidCoupon) {
        return;
      }
      if (product.value.cardCode) {
        swal.fire({
            icon: "error",
            text: t("cashier.alert.external-coupon"),
          });
        return;
      }
      store.commit("plusQuontityToOrderProduct", index);
    }
    function minusDiscount(index: number) {
      if (product.value.notValidCoupon) {
        return;
      }
      store.commit("minusDiscountToOrderProduct", index);
    }
    function plusDiscount(index: number) {
      if (product.value.notValidCoupon) {
        return;
      }
      store.commit("plusDiscountToOrderProduct", index);
    }
    async function checkDiscountByRestriction(
      event: any,
      product: OrderProduct
    ) {
      let newValue: string = event.value;
      if (!newValue) {
        newValue = "0";
      }
      const arr = newValue.split(".");

      if (arr.length > 1) {
        newValue = arr[0] + "." + arr[1].slice(0, 2);
      }
      product.priceLine.discount = await store.dispatch(
        "checkDiscountByRestriction",
        {
          product,
          newDiscountValue: newValue,
        }
      );
    }
    async function removeProduct(index: number) {
      if (product.value.notValidCoupon) {
        store.dispatch("removeProductFromOrder", index);
        return;
      }
      const swalAction = await swal.fire({
        icon: "info",
        text: t("cashier.alert.confirm-remove-product"),
        customClass: {
          confirmButton: "popup-bookit-button",
          cancelButton: "close-button-wrapper popup-bookit-button",
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: t("cashier.label.yes"),
        cancelButtonText: t("cashier.label.no"),
      });
      if (!swalAction.isConfirmed) {
        return;
      }
      store.dispatch("removeProductFromOrder", index);
    }
    function exchange(index: number) {
      if (product.value.notValidCoupon) {
        return;
      }
      store.commit("toChangeInBasketProduct", index);
      const productInStore = store.state.order.products[index];
      router.push({
        name: "productPriceLinesEditMode",
        params: {
          productId: productInStore.id,
          slotId: productInStore.slotId,
          dateStart: moment(productInStore.date).format("YYYY-MM-DD"),
        },
      });
    }
    function quantityFocus(event: FocusEvent) {
      const target = event.target as HTMLInputElement;
      qtyOldValue.value = Number(target?.value.toString().split(",").join(""));
    }

    async function quantityAdded(newValue: number, couponStore: Coupon) {
      const countAdded = newValue - qtyOldValue.value;

      // Validation
      if (couponStore.limitationType == CouponLimitationType.CustomerQuantity) {
        if (couponStore.leftLimitationTotal < countAdded * product.value.priceLine.qtyToUse) {
          swal.fire({
            icon: "error",
            text: t("cashier.alert.available-tickets-exceeded"),
          });
          await q.delay(100);
          product.value.priceLine.quantity = qtyOldValue.value;
          return;
        }
      } else {
        if (couponStore.discountAmount) {
          var discountAmountOrderCurrencyRate =
            countAdded * couponStore.discountAmount;
          if (
            couponStore.leftLimitationTotal < discountAmountOrderCurrencyRate
          ) {
            swal.fire({
              icon: "error",
              text: t("cashier.alert.available-tickets-exceeded"),
            });
            await q.delay(100);
            product.value.priceLine.quantity = qtyOldValue.value;
            return;
          }
        } else if (couponStore.discountPercent) {
          var discountAmountFromPercentOrderCurrencyRate =
            ((countAdded * product.value.priceLine.priceForOne) / 100) *
            couponStore.discountPercent;
          if (
            couponStore.leftLimitationTotal <
            discountAmountFromPercentOrderCurrencyRate
          ) {
            swal.fire({
              icon: "error",
              text: t("cashier.alert.available-tickets-exceeded"),
            });
            await q.delay(100);
            product.value.priceLine.quantity = qtyOldValue.value;
            return;
          }
        } else if (couponStore.ticketFixedUnitPrice) {
          var ticketAmountAmountOrderCurrencyRate =
            countAdded * couponStore.ticketFixedUnitPrice;
          if (
            couponStore.leftLimitationTotal <
            ticketAmountAmountOrderCurrencyRate
          ) {
            swal.fire({
              icon: "error",
              text: t("cashier.alert.available-tickets-exceeded"),
            });
            await q.delay(100);
            product.value.priceLine.quantity = qtyOldValue.value;
            return;
          }
        } else if (couponStore.discountType == CouponDiscountType.NoDiscount) {
          var noDiscountAmountOrderCurrencyRate =
            countAdded * product.value.priceLine.priceForOne;
          if (
            couponStore.leftLimitationTotal < noDiscountAmountOrderCurrencyRate
          ) {
            swal.fire({
              icon: "error",
              text: t("cashier.alert.available-tickets-exceeded"),
            });
            await q.delay(100);
            product.value.priceLine.quantity = qtyOldValue.value;
            return;
          }
        }
      }

      // Calculate coupon
      if (couponStore.limitationType == CouponLimitationType.CustomerQuantity) {
        couponStore.leftLimitationTotal -= countAdded * product.value.priceLine.qtyToUse;
      } else {
        if (store.state.scanValue) {
          couponStore.leftLimitationTotal -= countAdded;
        } else {
          let discount = 0;
          if (couponStore.discountAmount) {
            const totalDiscountInNIS = couponStore.discountAmount * countAdded;
            discount = totalDiscountInNIS;
          } else {
            var totalDiscountInNIS =
              ((product.value.priceLine.priceForOne * countAdded) / 100) *
              couponStore.discountPercent;
            discount = totalDiscountInNIS;
          }
          couponStore.leftLimitationTotal -= discount;
        }
      }

      // Calculate product discount
      if (couponStore.discountAmount) {
        var totalDiscountAmount = couponStore.discountAmount * countAdded;
        product.value.priceLine.discount += totalDiscountAmount;
      } else {
        var totalDiscountAmountFromPercent =
          ((product.value.priceLine.priceForOne * countAdded) / 100) *
          couponStore.discountPercent;
        product.value.priceLine.discount += totalDiscountAmountFromPercent;
      }
    }
    async function quantityRemoved(newValue: number, couponStore: Coupon) {
      const countRemoved = qtyOldValue.value - newValue;

      // Calculate coupon
      if (couponStore.limitationType == CouponLimitationType.CustomerQuantity) {
        couponStore.leftLimitationTotal += countRemoved * product.value.priceLine.qtyToUse;
      } else {
        if (store.state.scanValue) {
          couponStore.leftLimitationTotal += countRemoved;
        } else {
          let discount = 0;
          if (couponStore.discountAmount) {
            const totalDiscountInNIS =
              couponStore.discountAmount * countRemoved;
            discount = totalDiscountInNIS;
          } else {
            discount =
              ((product.value.priceLine.priceForOne * countRemoved) / 100) *
              couponStore.discountPercent;
          }
          couponStore.leftLimitationTotal += discount;
        }
      }

      // Calculate product discount
      if (couponStore.discountAmount) {
        var totalDiscountAmount = couponStore.discountAmount * countRemoved;
        product.value.priceLine.discount -= totalDiscountAmount;
      } else {
        var totalDiscountAmountFromPercent =
          ((product.value.priceLine.priceForOne * countRemoved) / 100) *
          couponStore.discountPercent;
        product.value.priceLine.discount -= totalDiscountAmountFromPercent;
      }
    }

    async function quantityBlur(event: InputNumberBlurEvent) {
      if (!product.value.priceLine.couponId) {
        return;
      }
      if (product.value.cardCode) {
        await q.delay(100);
        product.value.priceLine.quantity = qtyOldValue.value;
        return;
      }
      const couponStore = store.state.coupons.find(
        (x) => x.id == product.value.priceLine.couponId
      );
      if (!couponStore) {
        return;
      }
      const newValue = Number(event.value.toString().split(",").join(""));
      if (newValue == 0 || isNaN(newValue)) {
        await q.delay(100);
        product.value.priceLine.quantity = qtyOldValue.value;
        return;
      }
      console.log(qtyOldValue.value, newValue);
      if (qtyOldValue.value < newValue) {
        quantityAdded(newValue, couponStore);
      } else if (qtyOldValue.value > newValue) {
        quantityRemoved(newValue, couponStore);
      }
    }

    function getDisplayDate(d: string) {
      return moment(d).format("DD/MM/YYYY");
    }
    function getDisplayDateTime(d: string) {
      return moment(d, "HH:mm:ss").format("HH:mm");
    }
    async function changePriceForOne(
      d: InputNumberBlurEvent,
      defaultPriceForOne: number
    ) {
      const priceValue = d.value.replace(props.currencySign, "");
      if (!priceValue || priceValue == "0") {
        await q.delay(100);
        store.commit("changePriceForOne", {
          index: props.index,
          newValue: defaultPriceForOne,
        });
        product.value.priceLine.priceForOne = defaultPriceForOne;
      }
    }
    const isWarning = computed(() => {
      if (props.notAvailableProducts && props.notAvailableProducts.length) {
        var item = props.notAvailableProducts.find(
          (x) =>
            x.productId == product.value.id &&
            x.startTime ==
              product.value.date + "T" + product.value.slotStartTime &&
            x.notAvailableCustomerTypeIds.includes(
              props.productProp.priceLine.id
            )
        );
        if (!item) {
          return false;
        }
        if (!item.isAvailable) {
          return true;
        }
      }
      return false;
    });
    return {
      product,
      changeSlotByPriceDuration,
      getDisplayDate,
      exchange,
      removeProduct,
      minusQuontity,
      plusQuontity,
      minusDiscount,
      checkDiscountByRestriction,
      plusDiscount,
      canEditDiscount,
      changeUnitPrice,
      getDisplayDateTime,
      changePriceForOne,
      isWarning,
      quantityBlur,
      quantityFocus,
      qtyOldValue,
      isVoucherItem,
      couponExist,
      isImmutable,
    };
  },
});
